import { useState } from "react";
import { useParams } from "react-router-dom";
import { RoleForm, AssignPermissions } from "@/frontend/pages/roles/components";
import { roleService, permissionsService } from "@/frontend/services";
import type { RoleResponseDTO, PermissionResponseDTO, PaginationRequestDTO, FlatPaginationRequestDTO } from "@/shared/dto";
import { useGetId } from "@/frontend/hooks/useGetId";
import { usePagination } from "@/frontend/hooks";
import { Spin, Tabs, Button } from "@/frontend/components/common";
import { message } from "antd";
import type { TabProps } from "@/frontend/components/common";
import type { TablePaginationConfig } from "antd/es/table";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/frontend/stores";
const initialPagination: TablePaginationConfig = {
  pageSizeOptions: ["7", "10", "20", "50", "100"],
  showSizeChanger: true,
  showTotal: (total) => `Total: ${total} registros`,
};

const initialParams: FlatPaginationRequestDTO = { page: 1, pageSize: 100 };

export function UpdateRolePage() {
  const { id } = useParams<{ id: string }>();
  const [targetKeys, setTargetKeys] = useState<string[]>([]);
  const [updating, setUpdating] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { data: roleData, loading: roleLoading } = useGetId<RoleResponseDTO>({
    fetchFn: roleService.getById,
    redirectUrl: "/roles",
    errorMessage: "Error al cargar el rol",
    id,
  });

  // Permisos asignados al rol (para el lado derecho del Transfer)
  const { data: assignedPermissionsData, loading: assignedPermissionsLoading } = usePagination<PermissionResponseDTO>({
    fetchFn: permissionsService.getByRoleIdPaginate,
    initialParams: { ...initialParams, roleId: id } as FlatPaginationRequestDTO<{ roleId: string }>,
    initialPagination: initialPagination,
    errorMessage: "Error al cargar permisos asignados",
  });

  // Permisos NO asignados al rol (para el lado izquierdo del Transfer)
  const { data: availablePermissionsData, loading: availablePermissionsLoading } = usePagination<PermissionResponseDTO>({
    fetchFn: permissionsService.getNotAssignedToRolePaginate,
    initialParams: { ...initialParams, roleId: id } as FlatPaginationRequestDTO<{ roleId: string }>,
    initialPagination: initialPagination,
    errorMessage: "Error al cargar permisos disponibles",
  });

  if (roleLoading || assignedPermissionsLoading || availablePermissionsLoading) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm w-full">
        <div className="flex justify-center items-center py-12">
          <Spin size="large" tip="Cargando..." />
        </div>
      </div>
    );
  }

  if (!roleData) {
    return null;
  }

  const getNewPermissions = (): PermissionResponseDTO[] => {
    let newPermissions: PermissionResponseDTO[] = [];
    availablePermissionsData?.forEach((permission) => {
      if (targetKeys.includes(permission.id)) {
        newPermissions.push(permission);
      }
    });
    assignedPermissionsData?.forEach((permission) => {
      if (targetKeys.includes(permission.id)) {
        newPermissions.push(permission);
      }
    });
    return newPermissions;
  };

  const handleUpdatePermissions = async () => {
    if (!id) return;

    setUpdating(true);
    try {
      const response = await roleService.updatePermissions(id, { permissionIds: targetKeys });
      if (response.success) {
        message.success(response.data?.message || "Permisos actualizados correctamente");
        let newPermissions: PermissionResponseDTO[] = getNewPermissions();
        useAuthStore.getState().updatePermissions(newPermissions);
        navigate("/dashboard");
      } else {
        message.error(response.error || "Error al actualizar permisos");
      }
    } catch (error) {
      message.error("Error al actualizar permisos");
      console.error(error);
    } finally {
      setUpdating(false);
    }
  };

  const initialValues = {
    name: roleData.name,
    guardSlug: roleData.guardSlug,
    description: roleData.description || "",
    status: roleData.status as "active" | "inactive" | "deleted",
    platformAccess: roleData.platformAccess as "web" | "mobile" | "both",
  };

  const tabItems: TabProps["items"] = [
    {
      key: "1",
      label: "Información del Rol",
      children: <RoleForm roleId={id} initialValues={initialValues} />,
    },
    {
      key: "2",
      label: "Asignar Permisos",
      children: (
        <div>
          <AssignPermissions
            availablePermissions={availablePermissionsData || []}
            assignedPermissions={assignedPermissionsData || []}
            onTargetKeysChange={setTargetKeys}
          />
          <div className="mt-4 flex justify-end">
            <Button
              type="primary"
              onClick={handleUpdatePermissions}
              loading={updating}
            >
              Actualizar Permisos
            </Button>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm w-full">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Actualizar Rol</h2>
        <p className="text-gray-600 mt-1">
          Modifica la información del rol en la plataforma
        </p>
      </div>
      <Tabs items={tabItems} type="card" />
    </div>
  );
}
